import React, { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ClipboardPaste, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ClipboardPasteModal - Reusable component for pasting images from clipboard
 * 
 * Features:
 * - Auto-detects images in clipboard (screenshots, browser copies)
 * - Manual paste area for file explorer copies (Ctrl+V)
 * - Preview before upload
 * - Customizable upload handler
 * 
 * Usage:
 * ```jsx
 * <ClipboardPasteModal
 *   isOpen={showPasteModal}
 *   onClose={() => setShowPasteModal(false)}
 *   onUpload={async (file) => {
 *     // Handle file upload
 *     const formData = new FormData();
 *     formData.append('file', file);
 *     await axios.post('/api/upload', formData);
 *   }}
 *   title="Upload Door Type Drawing"
 * />
 * ```
 */
const ClipboardPasteModal = ({ 
  isOpen, 
  onClose, 
  onUpload, 
  title = "Paste Image",
  uploading = false 
}) => {
  const [previewState, setPreviewState] = useState({
    imageUrl: null,
    imageBlob: null,
    waitingForPaste: true
  });

  // Try to read from clipboard automatically when modal opens
  const tryAutoDetect = useCallback(async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.read) {
        const clipboardItems = await navigator.clipboard.read();
        
        for (const item of clipboardItems) {
          const imageType = item.types.find(type => type.startsWith('image/'));
          if (imageType) {
            const imageBlob = await item.getType(imageType);
            const imageUrl = URL.createObjectURL(imageBlob);
            
            setPreviewState({
              imageUrl,
              imageBlob,
              waitingForPaste: false
            });
            return;
          }
        }
      }
    } catch (error) {
      console.log('Clipboard API not available, showing paste area');
    }

    // No image found or API not available
    setPreviewState({
      imageUrl: null,
      imageBlob: null,
      waitingForPaste: true
    });
  }, []);

  // Handle modal open - auto-detect when modal opens
  React.useEffect(() => {
    if (isOpen) {
      // Auto-detect clipboard image when modal opens
      tryAutoDetect();
    } else {
      // Cleanup when modal closes
      if (previewState.imageUrl) {
        URL.revokeObjectURL(previewState.imageUrl);
      }
      setPreviewState({
        imageUrl: null,
        imageBlob: null,
        waitingForPaste: true
      });
    }
  }, [isOpen]);

  // Handle paste event in the paste area
  const handlePasteEvent = async (event) => {
    event.preventDefault();
    
    if (!previewState.waitingForPaste) return;

    const items = event.clipboardData?.items;
    const files = event.clipboardData?.files;
    
    let imageBlob = null;

    // First try to get image from items (for screenshots)
    if (items) {
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          imageBlob = item.getAsFile();
          break;
        }
      }
    }

    // If no image in items, check files (for file explorer copies)
    if (!imageBlob && files && files.length > 0) {
      for (const file of files) {
        if (file.type.startsWith('image/')) {
          imageBlob = file;
          break;
        }
      }
    }

    if (!imageBlob) {
      toast.error('No image found in clipboard. Please copy an image file or take a screenshot.');
      return;
    }

    // Create preview URL and update state
    const imageUrl = URL.createObjectURL(imageBlob);
    setPreviewState({
      imageUrl,
      imageBlob,
      waitingForPaste: false
    });
  };

  // Handle upload confirmation
  const handleUploadConfirm = async () => {
    if (!previewState.imageBlob || !onUpload) return;

    const file = new File(
      [previewState.imageBlob], 
      `clipboard-image-${Date.now()}.png`, 
      { type: previewState.imageBlob.type }
    );

    try {
      await onUpload(file);
      handleClose();
    } catch (error) {
      // Error handling is done in the parent component
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (previewState.imageUrl) {
      URL.revokeObjectURL(previewState.imageUrl);
    }
    setPreviewState({
      imageUrl: null,
      imageBlob: null,
      waitingForPaste: true
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {previewState.waitingForPaste ? title : 'Image Preview'}
          </DialogTitle>
          <DialogDescription>
            {previewState.waitingForPaste 
              ? 'Click the area below and press Ctrl+V (or Cmd+V) to paste your image'
              : 'Review the image before uploading'
            }
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {previewState.waitingForPaste ? (
            /* Paste Area - waiting for Ctrl+V */
            <div 
              className="border-2 border-dashed border-orange-300 rounded-lg p-8 bg-orange-50 flex flex-col items-center justify-center min-h-[300px] cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors hover:bg-orange-100"
              tabIndex={0}
              onPaste={handlePasteEvent}
              onClick={(e) => e.currentTarget.focus()}
            >
              <ClipboardPaste className="h-16 w-16 text-orange-400 mb-4" />
              <p className="text-lg font-medium text-orange-700 mb-2">Click here and press Ctrl+V</p>
              <p className="text-sm text-orange-600">Supports screenshots and images copied from file explorer</p>
            </div>
          ) : (
            /* Image Preview */
            <div className="border rounded-lg p-4 bg-slate-50 flex items-center justify-center min-h-[300px] max-h-[500px] overflow-auto">
              <img 
                src={previewState.imageUrl} 
                alt="Clipboard preview" 
                className="max-w-full max-h-[450px] object-contain"
              />
            </div>
          )}
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            {!previewState.waitingForPaste && (
              <Button 
                onClick={handleUploadConfirm}
                className="bg-[#FF6600] hover:bg-[#ff8533]"
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ClipboardPasteModal;
